import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const RecentlyViewedContext = createContext();
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'balmart_recently_viewed';
const MAX_ITEMS = 12;

export function RecentlyViewedProvider({ children }) {
  const [ids, setIds] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch { return []; }
  });
  const [products, setProducts] = useState([]);

  const persist = (newIds) => {
    setIds(newIds);
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(newIds)); } catch {}
  };

  const track = useCallback((productId) => {
    if (!productId) return;
    setIds(prev => {
      const filtered = prev.filter(id => id !== productId);
      const next = [productId, ...filtered].slice(0, MAX_ITEMS);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
  }, []);

  // Lazy-load product details when needed by consumers
  const reload = useCallback(async () => {
    if (ids.length === 0) { setProducts([]); return; }
    try {
      // Single request: fetch all products then filter — keeps simple, dataset is small (99 products)
      const res = await axios.get(`${API}/products`);
      const byId = Object.fromEntries((res.data || []).map(p => [p.product_id, p]));
      setProducts(ids.map(id => byId[id]).filter(Boolean));
    } catch {
      setProducts([]);
    }
  }, [ids]);

  useEffect(() => { reload(); }, [reload]);

  const clear = () => persist([]);

  return (
    <RecentlyViewedContext.Provider value={{ ids, products, track, clear }}>
      {children}
    </RecentlyViewedContext.Provider>
  );
}

export const useRecentlyViewed = () => useContext(RecentlyViewedContext);
